// Client-side mirror of the per-kind caps of the multipart upload route in
// backend/routes/uploads.js — checked before that transfer starts so an
// oversized video fails instantly. Direct-to-R2 uploads have larger caps,
// which the server applies at presign time before any bytes move.
export const UPLOAD_LIMITS_MB = {
    image: 10,
    video: 100,
    audio: 40,
};

const KIND_LABELS = {
    image: 'Image',
    video: 'Video',
    audio: 'Audio',
};

// The media kind of a file ('image' | 'video' | 'audio'), or null when the
// file is not an embeddable media type.
export const mediaKindForFile = (file) => {
    const type = typeof file?.type === 'string' ? file.type : '';
    if (type.startsWith('image/')) {
        return 'image';
    }
    if (type.startsWith('video/')) {
        return 'video';
    }
    if (type.startsWith('audio/')) {
        return 'audio';
    }
    return null;
};

// Returns an error message when the file exceeds its kind's cap, else null.
export const uploadSizeError = (file) => {
    const kind = mediaKindForFile(file);
    if (!kind) {
        return null;
    }

    const limitMb = UPLOAD_LIMITS_MB[kind];
    if (file.size > limitMb * 1024 * 1024) {
        return `${KIND_LABELS[kind]} exceeds ${limitMb}MB limit`;
    }
    return null;
};

export const HEIC_UNSUPPORTED_MESSAGE = 'iPhone HEIC photos aren\'t supported yet — export as JPEG and try again';

// iPhone photos straight from the camera roll. Browsers can't decode them
// (so they can't be resized or shown) and the server refuses them; some
// browsers report no MIME type at all, hence the extension check.
export const isHeicFile = (file) => {
    const type = typeof file?.type === 'string' ? file.type.toLowerCase() : '';
    if (/^image\/hei[cf](-sequence)?$/.test(type)) {
        return true;
    }
    const name = typeof file?.name === 'string' ? file.name : '';
    return /\.hei[cf]$/i.test(name);
};

// Upload refusals that would repeat for every remaining file of a batch: a
// missing or wrong writer passphrase (401/403, or the portal's own check
// before any request) and rate limiting (429). `status` is set by
// utils/directUpload.js.
const BATCH_STOPPING_STATUSES = new Set([401, 403, 429]);

export const stopsUploadBatch = (error) => (
    BATCH_STOPPING_STATUSES.has(error?.status)
    || /passphrase is required/i.test(typeof error?.message === 'string' ? error.message : '')
);

// Reason listed for the files left over once a batch has stopped.
export const UPLOAD_NOT_TRIED_REASON = 'not tried';

const fileLabel = (name) => (typeof name === 'string' && name.trim() ? name.trim() : 'unnamed file');

const sentence = (text) => (/[.!?…]$/.test(text) ? text : `${text}.`);

// Files sharing a reason are named once per reason; a long run of names is
// cut short so the summary stays readable.
const MAX_NAMES_PER_REASON = 5;

const listNames = (names) => (
    names.length <= MAX_NAMES_PER_REASON
        ? names.join(', ')
        : `${names.slice(0, MAX_NAMES_PER_REASON).join(', ')} and ${names.length - MAX_NAMES_PER_REASON} more`
);

const groupFailures = (failures) => {
    const namesByReason = new Map();
    failures.forEach(({ name, reason }) => {
        // Server messages often end in a full stop; the list adds its own.
        const key = (reason || 'upload failed').replace(/\.$/, '');
        if (!namesByReason.has(key)) {
            namesByReason.set(key, []);
        }
        namesByReason.get(key).push(fileLabel(name));
    });
    return Array.from(namesByReason, ([reason, names]) => `${listNames(names)} — ${reason}`);
};

/**
 * Writer-facing summary of a multi-file upload, or null when every file was
 * added. `failures` is a list of { name, reason } for files that were tried
 * and failed, skipped because they are not photo/video/audio files, or not
 * tried because the batch stopped. Files that share a reason are grouped.
 *   "Added 7 of 9 files. Failed: a.heic — …; b.png, c.png — …."
 */
export const summarizeUploadBatch = ({ total, added, failures = [] }) => {
    if (!failures.length) {
        return null;
    }

    const details = groupFailures(failures).join('; ');

    if (total === 1) {
        return sentence(`Couldn't add ${details}`);
    }

    return `Added ${added} of ${total} files. ${sentence(`Failed: ${details}`)}`;
};
